import { lazy, Suspense, useEffect, useState } from 'react'
import { ScreenPlaceholder } from '@components/placeholders/ScreenPlaceholder'
import {
  CONTACT_ROUTE,
  DOCS_ROUTE,
  DOWNLOAD_ROUTE,
  SUPPORT_ROUTE,
  LANDING_ROUTE,
  LOCK_ROUTE,
  LOGIN_ROUTE,
} from '@constants/routes'
import { sessionStore } from '@features/auth/services/sessionStore'
import { useBootSession } from '@features/auth/hooks/useBootSession'
import { useDocumentHead } from '@hooks/useDocumentHead'
import { useHashRoute } from '@hooks/useHashRoute'
import { useLanguageSync } from '@features/preferences/hooks/useLanguageSync'
import { usePreferencesSync } from '@features/preferences/hooks/usePreferencesSync'
import { useTheme } from '@features/preferences/hooks/useTheme'
import { useSessionIdleLock } from '@hooks/useSessionIdleLock'
import { AuthLayout } from '@layouts/AuthLayout'
import { MainLayout } from '@layouts/MainLayout'
import { LockPage } from '@/pages/LockPage'
import { LoginPage } from '@/pages/LoginPage'
import { HomePage } from '@/pages/HomePage'
import { ExportPage } from '@/pages/ExportPage'
import { GeneratorPage } from '@/pages/GeneratorPage'
import { ImportPage } from '@/pages/ImportPage'
import { ToolsLandingPage } from '@/pages/ToolsLandingPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { EmergencyVaultPage } from '@/pages/EmergencyVaultPage'
import { VaultDetailPage } from '@/pages/VaultDetailPage'
import { VaultEditPage } from '@/pages/VaultEditPage'
import { VaultListPage } from '@/pages/VaultListPage'
import { VaultNewPage } from '@/pages/VaultNewPage'
import { VaultTrashPage } from '@/pages/VaultTrashPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { SendListPage } from '@/pages/SendListPage'
import { SendNewPage } from '@/pages/SendNewPage'
import { SendReceivePage } from '@/pages/SendReceivePage'
import { MarketingLayout } from '@layouts/MarketingLayout'
import { ErrorBoundary } from '@shared/ui/ErrorBoundary'
import { ToastProvider } from '@shared/ui/ToastProvider'
import { isAuthRoute, isMarketingRoute, navigate } from '@utils/route'

/**
 * Bundle 分割方針 (R-43 bundle audit、H-3):
 *
 *   - LoginPage / LockPage / HomePage は eager (login → lock unlock / 認証直後の HomePage
 *     は遅延読み込みすると UX が悪化するため)
 *   - RegisterPage / ForgotResetPage は zxcvbn-ts (辞書 ~900 KB gzip) を含むため lazy
 *   - forgot/* hub および 3 サブ (recovery-key / emergency / hint) + terms / privacy は
 *     login 利用者のうちごく一部しか到達しない低頻度経路のため lazy。Login 直接の bundle
 *     からこれらを完全に切り離す。
 *
 * すべて React.lazy + Suspense fallback 空文字で UI 上のチラつきを最小化。
 */
const RegisterPage = lazy(() =>
  import('@/pages/RegisterPage').then(m => ({ default: m.RegisterPage })),
)

const ForgotResetPage = lazy(() =>
  import('@/pages/ForgotResetPage').then(m => ({ default: m.ForgotResetPage })),
)

const ForgotPage = lazy(() => import('@/pages/ForgotPage').then(m => ({ default: m.ForgotPage })))

const RecoveryKeyPage = lazy(() =>
  import('@/pages/RecoveryKeyPage').then(m => ({ default: m.RecoveryKeyPage })),
)

const EmergencyContactPage = lazy(() =>
  import('@/pages/EmergencyContactPage').then(m => ({ default: m.EmergencyContactPage })),
)

const ForgotHintPage = lazy(() =>
  import('@/pages/ForgotHintPage').then(m => ({ default: m.ForgotHintPage })),
)

const TermsPage = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })))

const PrivacyPage = lazy(() =>
  import('@/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })),
)

/**
 * T-M: marketing pages は login 直近に居る user の少数しか到達しないため lazy。
 * Phase 4 で Landing 実装時に landing.css の bundle (gradient + animation 含む)
 * を切り離す事も期待できる。
 */
const ContactPage = lazy(() =>
  import('@/pages/ContactPage').then(m => ({ default: m.ContactPage })),
)

const DocsPage = lazy(() => import('@/pages/DocsPage').then(m => ({ default: m.DocsPage })))

const DownloadPage = lazy(() =>
  import('@/pages/DownloadPage').then(m => ({ default: m.DownloadPage })),
)

const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then(m => ({ default: m.LandingPage })),
)

const SupportPage = lazy(() =>
  import('@/pages/SupportPage').then(m => ({ default: m.SupportPage })),
)

const REGISTER_ROUTE = 'register'
const TERMS_ROUTE = 'terms'
const PRIVACY_ROUTE = 'privacy'
const FORGOT_ROUTE = 'forgot'
const FORGOT_RECOVERY_KEY_ROUTE = 'forgot/recovery-key'
const FORGOT_EMERGENCY_ROUTE = 'forgot/emergency'
const FORGOT_HINT_ROUTE = 'forgot/hint'
const FORGOT_RESET_ROUTE = 'forgot/reset'

/**
 * App shell — hash router を最上位で 1 度だけ呼び、route に応じて layout / page を選択する。
 *
 *   - AUTH_ROUTES: AuthLayout + (LoginPage | RegisterPage | LockPage | TermsPage |
 *       PrivacyPage | ForgotPage | RecoveryKeyPage | EmergencyContactPage |
 *       ForgotHintPage | ForgotResetPage) — AUTH_ROUTES 10/10 完全配線済 (T-C5e closure)
 *   - 非 AUTH_ROUTES: MainLayout (RouteGuard 内蔵) + ScreenPlaceholder
 *   - 全体を <ToastProvider> でラップ — 任意の hook (`useToast`) からトースト表示可能
 *   - sessionStore.subscribe で認証状態を監視し、ログイン中のみ idleLock を起動
 *     (T-C1 で配線、L-13 idleLock 部分解消)。L-15 教訓に従い handle は useRef で保持し、
 *     再ログイン / unmount 時に旧インスタンスを必ず stop する。
 *
 * Phase 3 後半で screen が増えたら、本ファイルの分岐を route → page map に
 * 入れ替えるだけで済む構造にしている (App は描画分岐のみ、ロジックなし)。
 */
const Shell = (): JSX.Element => {
  const { route, go } = useHashRoute()

  // route / 言語に応じて <head> (title / lang / description / OG / Twitter) を更新する。
  useDocumentHead(route)

  // T-E1: Topbar 検索 input の現在値。route が変わるたびに空に戻す
  // (vault で打った query を send 等に持ち越さない UX 規範)。
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    setSearchQuery('')
  }, [route])

  // 「メールアドレスを保存」(trustDevice=true) チェック時の挙動:
  // ・useBootSession が `/api/auth/refresh` で LOCKED 状態を復元済
  // ・その状態で `#login` / `#register` / `#forgot/*` を直接開いた場合、
  //   AuthLayout はそのまま render してしまう (RouteGuard は MainLayout 系のみ)
  //   ため、ここで明示的に LOCKED → `#lock`、UNLOCKED → `#home` へ誘導する。
  // terms / privacy / lock / verify-email は既に正しい遷移先なので除外。
  useEffect(() => {
    const isRedirectableAuth =
      route === 'login' ||
      route === 'register' ||
      route === 'forgot' ||
      route === 'forgot/recovery-key' ||
      route === 'forgot/hint' ||
      route === 'forgot/emergency'

    if (!isRedirectableAuth) return
    if (sessionStore.isLocked()) navigate(LOCK_ROUTE)
    else if (sessionStore.isAuthenticated()) navigate('home')
  }, [route])

  if (isAuthRoute(route)) {
    // T-H2: `verify-email/<token>` は動的セグメントを持つ auth route。
    if (route.startsWith('verify-email/')) {
      const token = route.slice('verify-email/'.length)

      return (
        <AuthLayout>
          <VerifyEmailPage token={decodeURIComponent(token)} />
        </AuthLayout>
      )
    }

    return (
      <AuthLayout>
        {route === LOGIN_ROUTE ? (
          <LoginPage />
        ) : route === REGISTER_ROUTE ? (
          <Suspense fallback={null}>
            <RegisterPage />
          </Suspense>
        ) : route === LOCK_ROUTE ? (
          <LockPage />
        ) : route === TERMS_ROUTE ? (
          <Suspense fallback={null}>
            <TermsPage />
          </Suspense>
        ) : route === PRIVACY_ROUTE ? (
          <Suspense fallback={null}>
            <PrivacyPage />
          </Suspense>
        ) : route === FORGOT_ROUTE ? (
          <Suspense fallback={null}>
            <ForgotPage />
          </Suspense>
        ) : route === FORGOT_RECOVERY_KEY_ROUTE ? (
          <Suspense fallback={null}>
            <RecoveryKeyPage />
          </Suspense>
        ) : route === FORGOT_EMERGENCY_ROUTE ? (
          <Suspense fallback={null}>
            <EmergencyContactPage />
          </Suspense>
        ) : route === FORGOT_HINT_ROUTE ? (
          <Suspense fallback={null}>
            <ForgotHintPage />
          </Suspense>
        ) : route === FORGOT_RESET_ROUTE ? (
          <Suspense fallback={null}>
            <ForgotResetPage />
          </Suspense>
        ) : null}
      </AuthLayout>
    )
  }

  // T-M: marketing pages (#landing / #docs / #contact) — public, no auth required.
  if (isMarketingRoute(route)) {
    return (
      <MarketingLayout>
        {route === CONTACT_ROUTE ? (
          <Suspense fallback={null}>
            <ContactPage />
          </Suspense>
        ) : route === DOCS_ROUTE ? (
          <Suspense fallback={null}>
            <DocsPage />
          </Suspense>
        ) : route === DOWNLOAD_ROUTE ? (
          <Suspense fallback={null}>
            <DownloadPage />
          </Suspense>
        ) : route === SUPPORT_ROUTE ? (
          <Suspense fallback={null}>
            <SupportPage />
          </Suspense>
        ) : route === LANDING_ROUTE ? (
          <Suspense fallback={null}>
            <LandingPage />
          </Suspense>
        ) : (
          <ScreenPlaceholder route={route} />
        )}
      </MarketingLayout>
    )
  }

  // T-D-Vault-A/B/C: vault routes を pages に配線。
  // 動的 `vault/:id` (id は head の次セグメント) は VaultDetailPage に渡す。
  // `vault/:id/edit` は後続 commit で実装予定。
  // #send/view/:id/:encodedKey — 認証不要の受信ページ (MainLayout なし)
  if (route.startsWith('send/view/')) {
    const parts = route.split('/')
    const sendId = parts[2] ?? ''
    const encodedKey = parts[3] ?? ''

    return <SendReceivePage id={sendId} encodedKey={encodedKey} />
  }

  const isVaultFolderRoute = route.startsWith('vault/folder/')
  const activeFolderRouteId = isVaultFolderRoute ? (route.split('/')[2] ?? null) : null

  // Track F: emergency/vault/:id — grantee 視点で granter の vault を読み取り表示
  const isEmergencyVaultRoute = route.startsWith('emergency/vault/')
  const emergencyInviteId = isEmergencyVaultRoute ? (route.split('/')[2] ?? null) : null

  const isVaultTrashRoute = route === 'vault/trash'

  const vaultDetailId =
    route.startsWith('vault/') &&
    route !== 'vault/new' &&
    route !== 'vault/trash' &&
    !route.endsWith('/edit') &&
    !isVaultFolderRoute
      ? (route.split('/')[1] ?? '')
      : null

  // F4: vault/:id/edit — edit 用 route
  const vaultEditId =
    route.startsWith('vault/') && route.endsWith('/edit') && !isVaultFolderRoute
      ? (route.split('/')[1] ?? '')
      : null

  const isReportsRoute = route === 'reports' || route.startsWith('reports/')
  const isSettingsRoute = route === 'settings' || route.startsWith('settings/')
  // C-1: 検索 box は Vault 一覧 / フォルダ絞込ルートでのみ機能する。
  // 他ページ (Send / Reports / Home / Tools / Settings) は受け取り側の filter
  // 実装が無いため Topbar 検索 box ごと非表示にする (UX の嘘の解消)。
  const isVaultListRoute = route === 'vault' || isVaultFolderRoute

  const protectedContent =
    route === 'home' ? (
      <HomePage />
    ) : isReportsRoute ? (
      <ReportsPage route={route} />
    ) : isSettingsRoute ? (
      <SettingsPage route={route} />
    ) : route === 'send' ? (
      <SendListPage />
    ) : route === 'send/new' ? (
      <SendNewPage />
    ) : route === 'tools' ? (
      <ToolsLandingPage />
    ) : route === 'tools/generator' ? (
      <GeneratorPage />
    ) : route === 'tools/import' ? (
      <ImportPage />
    ) : route === 'tools/export' ? (
      <ExportPage />
    ) : route === 'vault' || isVaultFolderRoute ? (
      <VaultListPage activeFolderId={activeFolderRouteId} searchQuery={searchQuery} />
    ) : route === 'vault/new' ? (
      <VaultNewPage />
    ) : isVaultTrashRoute ? (
      <VaultTrashPage />
    ) : vaultEditId ? (
      <VaultEditPage id={vaultEditId} />
    ) : vaultDetailId ? (
      <VaultDetailPage id={vaultDetailId} />
    ) : isEmergencyVaultRoute && emergencyInviteId ? (
      <EmergencyVaultPage inviteId={emergencyInviteId} />
    ) : (
      <ScreenPlaceholder route={route} />
    )

  return (
    <MainLayout
      route={route}
      go={go}
      isAuthenticated={sessionStore.isAuthenticated()}
      isLocked={sessionStore.isLocked()}
      {...(isVaultListRoute ? { searchQuery, onSearchChange: setSearchQuery } : {})}
    >
      {protectedContent}
    </MainLayout>
  )
}

export default function App(): JSX.Element {
  // B5: 起動 / F5 後に refresh cookie から LOCKED セッションを復元する。
  // 復元完了まで Shell を描画しない (UNAUTH 状態で RouteGuard が #login へ
  // 誤誘導するのを防ぐ)。
  const booted = useBootSession()

  useSessionIdleLock()
  usePreferencesSync()
  useTheme()
  useLanguageSync()

  return (
    <ToastProvider>
      <ErrorBoundary>{booted ? <Shell /> : null}</ErrorBoundary>
    </ToastProvider>
  )
}
